import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { AddMembersRequestPayload } from 'api/billing/types';
import { Company, UserType } from 'api/models';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import {
  SUBSCRIPTION_KEY,
  useAddMembersEstimateMutation,
  useAddMembersMutation,
  useForceSyncSubscriptionMutation,
} from 'queries/billing';
import { ALL_MEMBERS_QUERY_KEY, ALL_USERS_QUERY_KEY } from 'queries/members';
import {
  ROLES_QUERY_KEY,
  useCreateRoleWithDefaultPlaceholderMutation,
  useVendorRolesQuery,
} from 'queries/roles';
import { logger } from 'services';
import { TeamMemberDrawerModal as InviteDrawer } from '../drawer-modal/drawer-modal';
import {
  PaymentStatus,
  PaymentStatuses,
  useManageAgentLicenses,
} from '../manage-license';
import { InviteComponentProps } from '../types';
import { InviteMemberFormPayload } from './form-v2';

const getAddMembersReqPayload = (
  formValues: InviteMemberFormPayload,
  company: Company,
): AddMembersRequestPayload => {
  const { email, role, permission, timeSheetApprover } = formValues;

  return email.map((emailObj) => {
    return {
      user: {
        emailId: emailObj.value,
        userType: UserType.Native,
        company: {
          companyId: company.companyId,
        },
        role: {
          roleId: role?.value,
        },
        timeSheetApprover: timeSheetApprover?.value,
      },
      permissionSet: {
        permissionSetType: permission?.value,
      },
    };
  });
};

export const TeamMembersInviteWrapper = ({
  subscription,
  drawerState,
  showMemberDetail,
  changeToEditMode,
  onClose,
  onUserInvited,
  drawerSectionProps,
  permissionOptions,
  roleOptions,
  isVendorInvite,
}: InviteComponentProps) => {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const successTitle = useTranslation('success');
  const errorTitle = useTranslation('failure');

  const [formValues, setFormValues] =
    useState<InviteMemberFormPayload | null>(null);

  const { company } = useUser();
  const { data: roles } = useVendorRolesQuery();
  const [showPaymentStatusProgress, setShowPaymentStatusProgress] =
    useState(false);

  const {
    mutateAsync: addMembers,
    isLoading: isInviting,
    status: addMembersMutationStatus,
    reset: resetAddMembersState,
  } = useAddMembersMutation();

  const { mutateAsync: createRole, isLoading: isCreatingRole } =
    useCreateRoleWithDefaultPlaceholderMutation();

  const { mutateAsync: forceSyncSubscription } =
    useForceSyncSubscriptionMutation();

  const onSuccess = useCallback(async () => {
    toastService({
      title: successTitle,
      message: intl.formatMessage(
        {
          id: 'settings.team_members.invite.success',
        },
        {
          number: formValues?.email.length,
        },
      ),
    }).success();

    onClose();
    onUserInvited();

    await forceSyncSubscription(subscription?.subscriptionId);
    await queryClient.invalidateQueries(SUBSCRIPTION_KEY);
    await queryClient.invalidateQueries('roles');
    await queryClient.invalidateQueries(ROLES_QUERY_KEY);
    await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
    await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
  }, [
    forceSyncSubscription,
    subscription.subscriptionId,
    formValues?.email.length,
    intl,
    queryClient,
    successTitle,
    onClose,
    onUserInvited,
  ]);

  const onError = useCallback(() => {
    toastService({
      title: errorTitle,
      message: intl.formatMessage({
        id: 'settings.team_members.invite.error',
      }),
    }).error();
  }, [errorTitle, intl]);

  const processRequest = useCallback(async () => {
    if (formValues === null) {
      return;
    }
    const formData = { ...formValues };

    try {
      if (formValues.role && (formValues.role as any)?.__isNew__) {
        const newRole = await createRole({
          roleName: String(formValues.role?.value),
        });

        formData.role = {
          label: newRole.roleName,
          value: newRole.roleId,
        };
      }

      const payload = getAddMembersReqPayload(formData, company);

      try {
        await addMembers(payload);

        onSuccess();
      } catch (e) {
        onError();
      }
    } catch (e) {
      logger.error('Add member failed: unable to create new role', {
        error: e,
      });
    }
  }, [addMembers, company, createRole, formValues, onError, onSuccess]);
  const { validateLicenseCount, isEstimateLoading } = useManageAgentLicenses({
    onLicenseCountValidated: ({ afterBillingConfirmation }) => {
      setShowPaymentStatusProgress(afterBillingConfirmation);
      processRequest();
    },
    estimateMutation: useAddMembersEstimateMutation,
  });

  const showLoader = useMemo(
    () => isInviting || isCreatingRole || isEstimateLoading,
    [isCreatingRole, isEstimateLoading, isInviting],
  );

  const onInviteSubmit = useCallback(
    async (_formValues) => {
      const formData = _formValues;

      setFormValues(formData);
      if (formData.role && (formData.role as any)?.__isNew__) {
        /**
         * For estimate call alone, exisiting role is mapped in request
         * instead of creating a new role.
         */
        formData.role = {
          label: roles?.[0].roleName,
          value: roles?.[0].roleId,
        };
      }
      validateLicenseCount(getAddMembersReqPayload(_formValues, company));
    },
    [company, roles, validateLicenseCount],
  );

  const paymentStatus = useMemo(() => {
    if (!showPaymentStatusProgress) {
      return undefined;
    }
    switch (addMembersMutationStatus) {
      case 'idle':
        return undefined;
      case 'loading':
        return PaymentStatuses.Processing;
      case 'success':
        return PaymentStatuses.Processed;
      case 'error':
        return PaymentStatuses.ProcessingFailed;
      default:
        return undefined;
    }
  }, [addMembersMutationStatus, showPaymentStatusProgress]);
  const onPaymentStatusClosed = useCallback(() => {
    resetAddMembersState();
    setShowPaymentStatusProgress(false);
  }, [resetAddMembersState]);

  return (
    <>
      <InviteDrawer
        drawerState={drawerState}
        onClose={onClose}
        showMemberDetail={showMemberDetail}
        changeToEditMode={changeToEditMode}
        onUserInvited={onUserInvited}
        onInviteSubmit={onInviteSubmit}
        showLoader={showLoader}
        drawerSectionProps={drawerSectionProps}
        permissionOptions={permissionOptions}
        roleOptions={roleOptions}
        isVendorInvite={isVendorInvite}
      />
      <PaymentStatus
        paymentStatus={paymentStatus}
        onPaymentStatusClosed={onPaymentStatusClosed}
      />
    </>
  );
};
